// services/civil.js — граждански договор: осигуровки, данък, нето и разход на
// възложителя (ПРОМТ-21). Чиста функция — ползват я екранът
// (src/calculators/civil.js), PDF-ът на сметката за изплатени суми
// (routes/civil.js) и тестовете (test/civil.test.js).
//
// Нормативната основа е сверена дословно в Ciela Net на 23.10.2026 (ДВ, бр.
// 97/23.10.2026) и проверена от втори агент:
//   • кога се дължат осигуровки — чл. 4, ал. 3, т. 5 и 6 КСО (само фонд
//     „Пенсии", без ОЗМ, безработица и ТЗПБ); пенсионерите — по желание
//     (чл. 4, ал. 6); самоосигуряващите се — нищо от възложителя (чл. 11,
//     ал. 1 НООСЛБГРЧМЛ; чл. 157, ал. 5 КСО; чл. 40, ал. 1, т. 2 ЗЗО);
//   • основата — след НПР по ЗДДФЛ (чл. 6, ал. 4 КСО), до максимума заедно
//     с другите доходи (чл. 6, ал. 11 КСО; чл. 40, ал. 8 ЗЗО);
//   • ставките и праговете — config/insurance.config.json (services/insurance.js);
//   • данъкът — чл. 43, ал. 1 и 3 ЗДДФЛ; ТЕЛК — чл. 43, ал. 2 и 9; IV
//     тримесечие — чл. 65, ал. 13; срокове — чл. 7, ал. 6 и чл. 158 КСО,
//     чл. 65, ал. 12 ЗДДФЛ.
//
// Нарочно ИЗВЪН обхвата (екранът ги казва с думи): избор по чл. 4б КСО,
// чуждестранни лица, възложител физическо лице, авансови плащания.

const { round2 } = require('./totals');
const holidays = require('./holidays');
const { periodFor, rates } = require('./insurance');
const { ACTIVITIES } = require('./chl55');
const { DISABILITY_THRESHOLD_EUR } = require('./rent');

// чл. 43, ал. 3 ЗДДФЛ — „данъчна ставка 10 на сто".
const TAX_RATE = 0.10;

function dfmt(iso) {
  const p = String(iso || '').split('-');
  return p.length === 3 ? p[2] + '.' + p[1] + '.' + p[0] : String(iso || '');
}
const eur = (n) => (Number(n) || 0).toFixed(2).replace('.', ',') + ' €';
const pct = (n) => String(n).replace('.', ',') + ' %';

function quarterOf(iso) {
  const m = Number(String(iso || '').slice(5, 7));
  return m ? Math.floor((m - 1) / 3) + 1 : null;
}

// 25-о число на месеца след плащането (чл. 7, ал. 6 КСО; чл. 158 КСО за УПФ;
// чл. 40, ал. 1, т. 3, б. „в" ЗЗО за здравните).
function insuranceDeadlineBase(paidOn) {
  let y = Number(paidOn.slice(0, 4));
  let m = Number(paidOn.slice(5, 7)) + 1;
  if (m > 12) { m = 1; y += 1; }
  return y + '-' + (m < 10 ? '0' : '') + m + '-25';
}
// Краят на месеца след тримесечието (чл. 65, ал. 12 ЗДДФЛ); IV — 31 януари
// (чл. 65, ал. 13, само при писмено желание за удържане).
function taxDeadlineBase(quarter, year) {
  if (quarter === 1) return year + '-04-30';
  if (quarter === 2) return year + '-07-31';
  if (quarter === 3) return year + '-10-31';
  return (year + 1) + '-01-31';
}
// Срок в неприсъствен ден → следващият работен; броенето е само в holidays.js.
function shifted(base, overrides, what, notes) {
  if (holidays.isWorkingDay(base, overrides)) return base;
  const d = holidays.nextWorkingDay(base, overrides);
  notes.push('Срокът ' + what + ' ' + dfmt(base) + ' е неприсъствен ден — мести се на ' + dfmt(d) + '.');
  return d;
}

// Сумата, разделена поравно на n части; последната поема остатъка от
// закръглянето, за да е сборът точно равен на цялото.
function splitEven(total, n) {
  const per = round2(total / n);
  const parts = [];
  for (let i = 0; i < n - 1; i++) parts.push(per);
  parts.push(round2(total - per * (n - 1)));
  return parts;
}

function civilContract(input) {
  const inp = input || {};
  const notes = [];
  const gross = round2(Math.max(0, Number(inp.gross) || 0));
  const act = ACTIVITIES.find((a) => a.key === inp.activity) || ACTIVITIES[0];
  const paidOn = String(inp.paidOn || '').slice(0, 10);
  const period = periodFor(paidOn); // хвърля ясна грешка извън конфигурацията
  const months = Math.max(1, Math.min(12, Math.floor(Number(inp.months) || 1)));
  const birthBefore1960 = !!inp.birthBefore1960;
  const selfInsured = !!inp.selfInsured;
  // При СОЛ другите отметки нямат значение — нищо не се удържа (екранът ги
  // крие; тук се пазим и от подаден отвън вход).
  const insuredElsewhere = !selfInsured && !!inp.insuredElsewhere;
  const pensioner = !selfInsured && !!inp.pensioner;
  const pensionerWants = pensioner && !!inp.pensionerWantsInsurance;
  const otherIncome = insuredElsewhere ? round2(Math.max(0, Number(inp.otherIncome) || 0)) : 0;

  // 1. НПР по чл. 29, ал. 1 ЗДДФЛ → осигурителният доход (чл. 6, ал. 4 КСО).
  const npr = round2(gross * act.rate / 100);
  const insurableBase = round2(gross - npr);

  // 2–4. Осигуровките — месец по месец (чл. 3, ал. 1 НЕВДПОВ), после сбор.
  const pensionRate = birthBefore1960 ? rates.pensionBefore1960 : rates.pensionAfter1959;
  const defs = [
    { key: 'pension', label: 'Пенсия (фонд „Пенсии")', r: pensionRate },
    // чл. 127, ал. 1 КСО — УПФ само за родените след 31.12.1959 г.
    birthBefore1960 ? null : { key: 'upf', label: 'Допълнителна пенсия (универсален пенсионен фонд)', r: rates.upf },
    { key: 'health', label: 'Здравна осигуровка', r: rates.health },
  ].filter(Boolean);
  const acc = {};
  defs.forEach((d) => { acc[d.key] = { key: d.key, label: d.label, ref: d.r.ref, base: 0, personRate: d.r.person, personAmount: 0, payerRate: d.r.payer, payerAmount: 0, due: false }; });

  const room = round2(Math.max(0, period.maxIncome - otherIncome));
  let insurableIncome = 0;
  let anyBelow = false;
  splitEven(insurableBase, months).forEach((mBase) => {
    const reachesMin = mBase >= period.minWage;
    if (!reachesMin) anyBelow = true;
    let full = false;
    let healthOnly = false;
    if (selfInsured) {
      // нищо — лицето се осигурява само (чл. 11, ал. 1 НООСЛБГРЧМЛ)
    } else if (pensioner && !pensionerWants) {
      // Пенсионерът не е „осигурен по т. 1 и 2а" на чл. 40, ал. 1 ЗЗО — за
      // здравната важи б. „а": прагът е МРЗ, освен ако е осигурен другаде.
      healthOnly = insuredElsewhere || reachesMin;
    } else {
      full = insuredElsewhere || reachesMin; // чл. 4, ал. 3, т. 6 / т. 5 КСО
    }
    if (!full && !healthOnly) return;
    const mIns = round2(Math.min(mBase, room));
    insurableIncome = round2(insurableIncome + mIns);
    defs.forEach((d) => {
      if (healthOnly && d.key !== 'health') return;
      const a = acc[d.key];
      a.due = true;
      a.base = round2(a.base + mIns);
      a.personAmount = round2(a.personAmount + round2(mIns * d.r.person / 100));
      a.payerAmount = round2(a.payerAmount + round2(mIns * d.r.payer / 100));
    });
  });
  const rows = defs.map((d) => acc[d.key]).filter((a) => a.due).map((a) => {
    const { due, ...rest } = a;
    return rest;
  });
  const insured = rows.length > 0;
  const personTotal = round2(rows.reduce((s, r) => s + r.personAmount, 0));
  const payerTotal = round2(rows.reduce((s, r) => s + r.payerAmount, 0));
  const capped = insured && insurableIncome < insurableBase;

  // Защо има (или няма) осигуровки — с думи, за човек, който не е счетоводител.
  const minTxt = eur(period.minWage);
  if (selfInsured) {
    notes.push('Самоосигуряващо се лице с декларация: възложителят не удържа и не внася осигуровки (чл. 11, ал. 1 НООСЛБГРЧМЛ; чл. 157, ал. 5 КСО; чл. 40, ал. 1, т. 2 ЗЗО). Лицето само се осигурява върху тези доходи при годишното изравняване.');
  } else if (pensioner && !pensionerWants) {
    notes.push('Пенсионер — за пенсия се осигурява само по желание (чл. 4, ал. 6 КСО). Без желание не се дължат вноски за фонд „Пенсии" и за допълнителна пенсия.');
    if (insured) notes.push('Дължи се само здравната осигуровка (чл. 40, ал. 1, т. 3 ЗЗО).');
    else notes.push('Под минималната работна заплата (' + minTxt + ') след разходите — осигуровки от възложителя не се дължат.');
  } else if (insuredElsewhere) {
    notes.push('Лицето е осигурено и на друго основание този месец — осигуровки се дължат независимо от сумата (чл. 4, ал. 3, т. 6 КСО; чл. 40, ал. 1, т. 3, б. „б" ЗЗО).');
  } else if (!insured) {
    notes.push('Под минималната работна заплата (' + minTxt + ') след разходите — осигуровки от възложителя не се дължат; лицето само внася здравни вноски по чл. 40, ал. 5 ЗЗО.');
  } else {
    notes.push('Възнаграждението след разходите е поне минималната работна заплата (' + minTxt + ') — осигуровки се дължат (чл. 4, ал. 3, т. 5 КСО; чл. 40, ал. 1, т. 3, б. „а" ЗЗО).');
    if (anyBelow) notes.push('В някои от месеците сумата е под минималната заплата — за тях осигуровки не се дължат.');
  }
  if (insured && otherIncome >= period.maxIncome) {
    notes.push('Максимумът е достигнат на другото основание (' + eur(period.maxIncome) + ') — тук осигуровки не се дължат (чл. 6, ал. 11 КСО). Лицето декларира дохода си пред възложителя (чл. 4 НЕВДПОВ).');
  } else if (capped) {
    notes.push('Осигуровките са върху ' + eur(insurableIncome) + ' — до максималния осигурителен доход ' + eur(period.maxIncome) + ' за месеца' + (otherIncome > 0 ? ', заедно с дохода на другото място' : '') + ' (чл. 6, ал. 11 КСО; чл. 40, ал. 8 ЗЗО).');
  }
  if (months > 1) {
    notes.push('Възнаграждението е за ' + months + ' месеца — разделено е поравно и всеки месец е сметнат отделно. По чл. 3, ал. 1 НЕВДПОВ то се разпределя по отработените дни — при неравни дни сметнете ръчно.');
  }

  // 5. Данъкът по чл. 43 ЗДДФЛ.
  const quarter = quarterOf(paidOn);
  const year = Number(paidOn.slice(0, 4));
  const taxableAfterContrib = round2(Math.max(0, insurableBase - personTotal));
  let taxBase = taxableAfterContrib;
  let taxDue = true;
  if (selfInsured) {
    taxBase = 0;
    taxDue = false;
    notes.push('Данък не се удържа (чл. 43, ал. 5 ЗДДФЛ) — лицето само внася данъка по чл. 43, ал. 6.');
    notes.push('Сметка за изплатени суми не се издава (чл. 45, ал. 6 ЗДДФЛ).');
  } else {
    if (inp.reducedCapacity) {
      // Същото правило като в services/rent.js — праг 4049,43 € от началото
      // на годината, данък само върху превишението (чл. 43, ал. 2 и 9).
      const total = round2((Number(inp.ytdTaxableIncome) || 0) + taxBase);
      if (total <= DISABILITY_THRESHOLD_EUR) {
        taxBase = 0;
        notes.push('Намалена работоспособност ≥ 50 % — данък се дължи едва след като облагаемият доход от началото на годината превиши ' + eur(DISABILITY_THRESHOLD_EUR) + '. Прагът не е достигнат — данък не се удържа.');
      } else {
        const excess = round2(total - DISABILITY_THRESHOLD_EUR);
        if (excess < taxBase) {
          taxBase = excess;
          notes.push('Намалена работоспособност ≥ 50 % — прагът ' + eur(DISABILITY_THRESHOLD_EUR) + ' се превишава с това плащане: данък само върху превишението.');
        }
      }
    }
    if (quarter === 4 && !inp.quarter4Withholding) {
      taxDue = false;
      notes.push('Четвърто тримесечие — данък не се удържа и не се внася, освен ако лицето писмено декларира, че желае удържане (чл. 65, ал. 13 ЗДДФЛ). Лицето го плаща с годишната си декларация.');
    } else if (quarter === 4) {
      notes.push('Четвърто тримесечие — лицето писмено е поискало удържане (чл. 65, ал. 13 ЗДДФЛ): данъкът се внася до 31 януари.');
    }
  }
  const tax = taxDue ? round2(taxBase * TAX_RATE) : 0;

  // 6. Нето и разход. Вноските за сметка на възложителя не се удържат от
  // възнаграждението (чл. 6, ал. 13 КСО) — затова влизат само в разхода.
  const net = round2(gross - personTotal - tax);
  const cost = round2(gross + payerTotal);

  // 7. Срокове.
  const deadlines = { insurance: null, insuranceBase: null, tax: null, taxBase: null };
  if (personTotal + payerTotal > 0) {
    deadlines.insuranceBase = insuranceDeadlineBase(paidOn);
    deadlines.insurance = shifted(deadlines.insuranceBase, inp.overrides, 'за осигуровките', notes);
  }
  if (tax > 0) {
    deadlines.taxBase = taxDeadlineBase(quarter, year);
    deadlines.tax = shifted(deadlines.taxBase, inp.overrides, 'за данъка', notes);
  }

  return {
    gross, activity: act.key, activityLabel: act.label, activityRef: act.ref,
    nprRate: act.rate, npr, insurableBase, insurableIncome, insured, capped,
    minWage: period.minWage, maxIncome: period.maxIncome, currency: period.currency,
    paidOn, months, quarter, year,
    rows, personTotal, payerTotal,
    taxableAfterContrib, taxBase, taxRate: TAX_RATE * 100, tax, net, cost,
    deadlines, statementAllowed: !selfInsured, notes,
  };
}

// ---- Нето → бруто (ПРОМТ-21а) ----
//
// Клиентът често знае само „колко да получи човекът на ръка". Обратната
// сметка НЕ е втора сметка: тя само предлага брута по формула и ги проверява
// с civilContract — единствената права сметка. Така закръглянето до цент,
// таванът, прагът на МРЗ и сроковете не могат да се разминат между двете.
//
// Нетото е на парчета: без осигуровки, само здравна, пълни осигуровки, и при
// последните две — с таван на осигурителния доход. При първото бруто над МРЗ
// нетото ПАДА (764,90 → 698,04 при 25 % НПР), тоест участъците се застъпват:
// едно нето може да има две решения (без и с осигуровки). Обход на всички
// нета 0,01–4000 € в 10 случая не намери нето без точно решение, но пътят
// „няма точно" остава — при друга конфигурация закръглянето може да прескочи цент.

const NET_SEARCH_STEP = 0.01;
// Колко далеч от формулата се търси: закръглянето на отделните удръжки
// размества нетото с няколко цента, ±1 € е с голям запас.
const NET_SEARCH_WINDOW = 1.00;

// Режимът на сметката — кои вноски се дължат. Две брута в един режим са едно
// и също решение (съседни центове често дават едно нето), не алтернатива.
const regimeOf = (r) => (r.rows || []).map((x) => x.key).join('+');

// Ядрото на търсенето: обхожда брутата около кандидатите и избира. `run(g)`
// е правата сметка (изнесено, за да се провери пътят „няма точно решение").
function solveNet(targetNet, candidates, run) {
  const stepC = Math.round(NET_SEARCH_STEP * 100);
  const winC = Math.round(NET_SEARCH_WINDOW * 100);
  const seen = new Map();
  candidates.forEach((cand) => {
    if (!Number.isFinite(cand) || cand <= 0) return;
    const c0 = Math.round(cand * 100);
    for (let c = c0 - winC; c <= c0 + winC; c += stepC) {
      if (c < 1 || seen.has(c)) continue;
      seen.set(c, run(c / 100));
    }
  });
  const all = [...seen.values()].sort((a, b) => a.gross - b.gross);
  const exact = all.filter((r) => r.net === targetNet);
  if (exact.length) {
    // Най-малкото бруто е най-евтиното за възложителя; от всеки друг режим —
    // по едно (най-малкото) като алтернатива.
    const solved = exact[0];
    const byRegime = new Map();
    exact.forEach((r) => { const k = regimeOf(r); if (k !== regimeOf(solved) && !byRegime.has(k)) byRegime.set(k, r); });
    const alternatives = [...byRegime.values()].map((r) => ({
      gross: r.gross, net: r.net, insured: !!r.insured, cost: r.cost,
      note: 'Същото нето се получава и при брутна сума ' + eur(r.gross)
        + (r.insured && !solved.insured ? ' (тогава лицето е осигурено)' : r.insured ? ' (с други осигуровки)' : ' (тогава без осигуровки)')
        + ' — изберете според случая.',
    }));
    return Object.assign({}, solved, { targetNet, solvedGross: solved.gross, exact: true, alternatives, solveNote: null });
  }
  const above = all.find((r) => r.net >= targetNet);
  if (!above) throw new Error('Не намерих брутна сума за нето ' + eur(targetNet) + ' — проверете данните.');
  const diff = round2(above.net - targetNet);
  // Над 5 цента разликата идва от прага на МРЗ; под тях — от закръглянето.
  const reason = diff > 0.05
    ? 'над МРЗ след разходите се дължат осигуровки.'
    : 'закръглянето на отделните удръжки до цент прескача тази стойност.';
  return Object.assign({}, above, {
    targetNet, solvedGross: above.gross, exact: false, alternatives: [],
    solveNote: 'Точно ' + eur(targetNet) + ' нето не може да се получи — при бруто ' + eur(above.gross)
      + ' нетото е ' + eur(above.net) + ' (с ' + eur(diff) + ' повече). Причина: ' + reason,
  });
}

// Входът е като на civilContract, но с `net` вместо `gross` (подаден `gross`
// се игнорира). Връща civilContract на намереното бруто + { targetNet,
// solvedGross, exact, alternatives[], solveNote }.
function grossFromNet(input) {
  const inp = Object.assign({}, input || {});
  const targetNet = round2(Number(inp.net) || 0);
  if (!(targetNet > 0)) throw new Error('Въведете нетна сума над 0 — колко да получи човекът на ръка.');
  delete inp.net;
  delete inp.gross;
  const run = (g) => civilContract(Object.assign({}, inp, { gross: g }));
  // Пробната сметка проверява датата (грешка извън конфигурацията) и дава
  // параметрите на периода — вида дейност, тавана, броя месеци.
  const probe = run(targetNet);
  const k = 1 - probe.nprRate / 100;
  const before = !!inp.birthBefore1960;
  const other = (!inp.selfInsured && inp.insuredElsewhere) ? Math.max(0, Number(inp.otherIncome) || 0) : 0;
  const cap = probe.months * Math.max(0, probe.maxIncome - other);
  const pension = before ? rates.pensionBefore1960 : rates.pensionAfter1959;
  const full = (pension.person + (before ? 0 : rates.upf.person) + rates.health.person) / 100;
  const ps = [0, rates.health.person / 100, full];
  const candidates = [];
  ps.forEach((p) => {
    [TAX_RATE, 0].forEach((t) => {
      // без таван: net = g − g·k·p − t·(g·k − g·k·p)
      candidates.push(targetNet / (1 - k * p - t * k * (1 - p)));
      // с таван: личните вноски са cap·p
      candidates.push((targetNet + cap * p - t * cap * p) / (1 - t * k));
    });
    if (inp.reducedCapacity) {
      // ТЕЛК над прага: данък 10 % само върху превишението над прага.
      const off = TAX_RATE * ((Number(inp.ytdTaxableIncome) || 0) - DISABILITY_THRESHOLD_EUR);
      candidates.push((targetNet + off) / (1 - k * p - TAX_RATE * k * (1 - p)));
      candidates.push((targetNet + cap * p * (1 - TAX_RATE) + off) / (1 - TAX_RATE * k));
    }
  });
  return solveNet(targetNet, candidates, run);
}

module.exports = { civilContract, grossFromNet, solveNet, NET_SEARCH_STEP, TAX_RATE, pct };
